import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";

/**
 * Consolidated receipt for a closed session (multiple orders for one venue).
 */
export default function SessionReceiptPrint() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/admin/sessions/${id}`).then(r => setSession(r.data)).catch(e => setError(e.response?.data?.detail || "Сессия не найдена"));
  }, [id]);

  useEffect(() => {
    if (session) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [session]);

  if (error) return <div style={{ padding: 20 }}>{error}</div>;
  if (!session) return <div style={{ padding: 20 }}>Загрузка...</div>;

  const closedAt = new Date(session.closed_at).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  // Merge all items across orders, separating menu and services
  const allItems = (session.orders || []).flatMap(o => o.items || []);
  const menuItems = allItems.filter(i => (i.item_type || "menu") === "menu");
  const serviceItems = allItems.filter(i => i.item_type === "service");

  // Aggregate by name (sum quantities of identical items)
  const aggregate = (items) => {
    const map = {};
    for (const it of items) {
      const key = `${it.name}|${it.price}|${it.package_name_ru || ""}`;
      if (!map[key]) map[key] = { ...it, quantity: 0 };
      map[key].quantity += (it.quantity || 1);
    }
    return Object.values(map);
  };
  const aggMenu = aggregate(menuItems);
  const aggServices = aggregate(serviceItems);

  return (
    <>
      <style>{`
        @page { size: 80mm auto; margin: 0; }
        @media print {
          body { margin: 0 !important; background: white !important; }
          .no-print { display: none !important; }
          .receipt { box-shadow: none !important; padding: 4mm !important; }
        }
        body { background: #f0f0f0; font-family: 'Courier New', Courier, monospace; }
        .receipt {
          width: 72mm; padding: 6mm 4mm; background: white; color: #000;
          margin: 20px auto; box-shadow: 0 4px 24px rgba(0,0,0,0.1);
          font-size: 12px; line-height: 1.4;
        }
        .receipt h1 { font-size: 14px; margin: 0 0 2px; text-align: center; letter-spacing: 1px; }
        .receipt .sub { text-align: center; font-size: 11px; margin-bottom: 6px; }
        .receipt hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
        .receipt .row { display: flex; justify-content: space-between; gap: 8px; }
        .receipt .item { margin-bottom: 4px; }
        .receipt .item .name { font-weight: bold; }
        .receipt .item .meta { display: flex; justify-content: space-between; font-size: 11px; }
        .receipt .pkg-tag { font-size: 10px; color: #555; font-style: italic; }
        .receipt .total { font-size: 14px; font-weight: bold; margin-top: 4px; }
        .receipt .footer { text-align: center; margin-top: 8px; font-size: 11px; }
        .toolbar { text-align: center; margin: 12px 0; }
        .toolbar button { padding: 8px 16px; margin: 0 4px; cursor: pointer; border: 1px solid #888; background: #fff; }
      `}</style>

      <div className="toolbar no-print">
        <button onClick={() => window.print()} data-testid="print-btn">🖨 Печать</button>
        <button onClick={() => window.close()}>Закрыть</button>
      </div>

      <div className="receipt" data-testid="session-receipt">
        <h1>ECO VILLAGE KZO</h1>
        <div className="sub">Итоговый счёт</div>

        <hr />

        <div className="row"><span>Место</span><span>{session.venue_name}</span></div>
        <div className="row"><span>Заказов</span><span>{(session.orders || []).length}</span></div>
        <div className="row"><span>Закрыто</span><span>{closedAt}</span></div>

        {session.venue_type === "house" && (session.bath_cost > 0 || session.extra_cost > 0) && (
          <>
            <hr />
            <div style={{ fontWeight: "bold", marginBottom: 4 }}>ДОМИК</div>
            {session.bath_cost > 0 && (
              <div className="item">
                <div className="meta">
                  <span>
                    {session.bath_minutes} мин (1-й час {formatPrice(session.price_per_hour || 0)}
                    {session.bath_minutes > 60 ? ` + ${session.bath_minutes - 60} мин × ${formatPrice(Math.round(session.price_per_minute || 0))}` : ""})
                  </span>
                  <span>{formatPrice(session.bath_cost)}</span>
                </div>
              </div>
            )}
            {session.extra_adult_cost > 0 && (
              <div className="item">
                <div className="meta">
                  <span>Доп. взрослый × {session.extra_adults} ({formatPrice(session.extra_adult_per_hour || 0)}/час)</span>
                  <span>{formatPrice(session.extra_adult_cost)}</span>
                </div>
              </div>
            )}
            {session.extra_child_cost > 0 && (
              <div className="item">
                <div className="meta">
                  <span>Доп. детский × {session.extra_children} ({formatPrice(session.extra_child_per_hour || 0)}/час)</span>
                  <span>{formatPrice(session.extra_child_cost)}</span>
                </div>
              </div>
            )}
          </>
        )}

        {aggMenu.length > 0 && (
          <>
            <hr />
            <div style={{ fontWeight: "bold", marginBottom: 4 }}>КУХНЯ И БАР</div>
            {aggMenu.map((it, i) => (
              <div key={i} className="item">
                <div className="name">{it.name || "—"}</div>
                {it.package_name_ru && <div className="pkg-tag">в составе: {it.package_name_ru}</div>}
                <div className="meta">
                  <span>{it.quantity} × {formatPrice(it.price)}</span>
                  <span>{formatPrice((it.price || 0) * (it.quantity || 1))}</span>
                </div>
              </div>
            ))}
          </>
        )}

        {aggServices.length > 0 && (
          <>
            <hr />
            <div style={{ fontWeight: "bold", marginBottom: 4 }}>УСЛУГИ</div>
            {aggServices.map((it, i) => (
              <div key={i} className="item">
                <div className="name">{it.name || "—"}</div>
                {it.package_name_ru && <div className="pkg-tag">в составе: {it.package_name_ru}</div>}
                <div className="meta">
                  <span>{it.quantity} × {formatPrice(it.price)}</span>
                  <span>{formatPrice((it.price || 0) * (it.quantity || 1))}</span>
                </div>
              </div>
            ))}
          </>
        )}

        <hr />
        <div className="row total">
          <span>ИТОГО</span>
          <span>{formatPrice(session.total_amount)}</span>
        </div>

        <hr />
        <div className="footer">
          Спасибо за визит!<br />
          ecovillage-kzo.kz
        </div>
      </div>
    </>
  );
}
